import type { InputHTMLAttributes, ReactNode, TextareaHTMLAttributes } from "react";

interface FieldShellProps {
  id: string;
  label: string;
  error?: string;
  hint?: string;
  children: ReactNode;
}

const FieldShell = ({ id, label, error, hint, children }: FieldShellProps) => (
  <div className="space-y-1.5">
    <label htmlFor={id} className="block text-xs font-bold uppercase tracking-wider text-slate-400 font-body">
      {label}
    </label>
    {children}
    {error ? (
      <p id={`${id}-error`} className="text-sm text-burgundy font-medium mt-1">
        {error}
      </p>
    ) : hint ? (
      <p id={`${id}-hint`} className="text-sm text-slate-300 mt-1">
        {hint}
      </p>
    ) : null}
  </div>
);
 
const inputClassName =
  "w-full rounded-xl border border-stone-200 bg-white px-4 py-2.5 text-base font-body text-ink transition-all duration-200 placeholder:text-stone-400 focus:border-[#0293cc] focus:ring-4 focus:ring-[#0293cc]/10 outline-none";

export const InputField = ({
  id,
  label,
  error,
  hint,
  className = "",
  ...props
}: InputHTMLAttributes<HTMLInputElement> & {
  id: string;
  label: string;
  error?: string;
  hint?: string;
}) => (
  <FieldShell id={id} label={label} error={error} hint={hint}>
    <input
      id={id}
      className={`${inputClassName} ${className}`.trim()}
      aria-invalid={error ? "true" : "false"}
      aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
      {...props}
    />
  </FieldShell>
);

export const TextareaField = ({
  id,
  label,
  error,
  hint,
  className = "",
  ...props
}: TextareaHTMLAttributes<HTMLTextAreaElement> & {
  id: string;
  label: string;
  error?: string;
  hint?: string;
}) => (
  <FieldShell id={id} label={label} error={error} hint={hint}>
    <textarea
      id={id}
      className={`${inputClassName} min-h-[180px] resize-y ${className}`.trim()}
      aria-invalid={error ? "true" : "false"}
      aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
      {...props}
    />
  </FieldShell>
);
